import jsPDF from 'jspdf';
import { Measurement, Client } from '../types';
import { MEASUREMENT_FIELDS } from '../constants';

const formatPdfMeasurementValue = (value: unknown): string => {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) {
        return value
            .map((entry) => String(entry).trim())
            .filter((entry) => entry !== '')
            .join(' / ');
    }
    return String(value);
};

/**
 * Export a single measurement to PDF
 * @param measurement The measurement to export
 * @param client The client associated with this measurement
 */
export const exportMeasurementToPDF = (measurement: Measurement, client: Client): void => {
    const doc = new jsPDF();

    // Header
    doc.setFillColor(67, 56, 202); // Indigo
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('Carnet de Mesures', 105, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.text('Fiche de mesures professionnelle', 105, 32, { align: 'center' });

    // Client info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text('Informations client', 20, 55);

    doc.setFontSize(11);
    doc.text(`Nom : ${client.name}`, 20, 65);
    doc.text(`Genre : ${client.gender}`, 20, 72);
    if (client.contact) {
        doc.text(`Contact : ${client.contact}`, 20, 79);
    }

    // Measurement info
    const date = measurement.date ? new Date(measurement.date) : null;
    const formattedDate =
        date && !Number.isNaN(date.getTime())
            ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
            : 'Date inconnue';

    doc.setFontSize(16);
    doc.text('Mesures', 20, 95);

    doc.setFontSize(11);
    doc.text(`Type de vêtement : ${measurement.outfitType}`, 20, 105);
    doc.text(`Date : ${formattedDate}`, 20, 112);

    // Measurements table
    const fields = MEASUREMENT_FIELDS[measurement.outfitType];
    let yPosition = 125;

    // Table header
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPosition - 5, 170, 8, 'F');
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Mesure', 25, yPosition);
    doc.text('Valeur (cm)', 140, yPosition);
    doc.setFont(undefined, 'normal');

    yPosition += 10;

    // Add measurements
    fields.forEach((field) => {
        const value = measurement[field.key];
        const formattedValue = formatPdfMeasurementValue(value);
        if (formattedValue !== '') {
            // Alternate row background
            if (fields.indexOf(field) % 2 === 0) {
                doc.setFillColor(250, 250, 250);
                doc.rect(20, yPosition - 5, 170, 7, 'F');
            }

            doc.text(field.label, 25, yPosition);
            doc.text(formattedValue, 140, yPosition);
            yPosition += 7;

            // New page if needed
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
            }
        }
    });

    // Notes
    if (measurement.notes && measurement.notes.trim() !== '') {
        yPosition += 10;
        if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Notes', 20, yPosition);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);

        yPosition += 7;
        const splitNotes = doc.splitTextToSize(measurement.notes, 170);
        doc.text(splitNotes, 20, yPosition);
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `Page ${i} sur ${pageCount} - Généré le ${new Date().toLocaleDateString('fr-FR')}`,
            105,
            290,
            { align: 'center' }
        );
    }

    // Download
    const fileName = `${client.name.replace(/[^a-z0-9]/gi, '_')}_${measurement.outfitType}_${formattedDate.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    doc.save(fileName);
};

/**
 * Export all measurements for a client to PDF
 * @param client The client with all measurements
 */
export const exportAllMeasurementsToPDF = (client: Client): void => {
    const doc = new jsPDF();

    // Header
    doc.setFillColor(67, 56, 202);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('Carnet de Mesures', 105, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.text('Récapitulatif complet des mesures', 105, 32, { align: 'center' });

    // Client info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text('Informations client', 20, 55);

    doc.setFontSize(11);
    doc.text(`Nom : ${client.name}`, 20, 65);
    doc.text(`Genre : ${client.gender}`, 20, 72);
    if (client.contact) {
        doc.text(`Contact : ${client.contact}`, 20, 79);
    }

    let yPosition = 95;

    const measurements = Array.isArray(client.measurements) ? client.measurements : [];
    const sortedMeasurements = measurements
        .filter((m) => !m.isDeleted)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (sortedMeasurements.length === 0) {
        doc.setFontSize(12);
        doc.text('Aucune mesure enregistrée.', 20, yPosition);
    } else {
        sortedMeasurements.forEach((measurement, index) => {
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }

            const date = measurement.date ? new Date(measurement.date) : null;
            const formattedDate =
                date && !Number.isNaN(date.getTime())
                    ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
                    : 'Date inconnue';

            // Measurement header
            doc.setFillColor(67, 56, 202);
            doc.rect(20, yPosition - 5, 170, 10, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text(`${measurement.outfitType} - ${formattedDate}`, 25, yPosition + 2);
            doc.setFont(undefined, 'normal');

            yPosition += 12;
            doc.setTextColor(0, 0, 0);

            const fields = MEASUREMENT_FIELDS[measurement.outfitType];
            const filledFields = fields.filter((field) => {
                const value = measurement[field.key];
                return formatPdfMeasurementValue(value) !== '';
            });

            if (filledFields.length > 0) {
                doc.setFontSize(9);
                filledFields.forEach((field, idx) => {
                    if (yPosition > 275) {
                        doc.addPage();
                        yPosition = 20;
                    }

                    if (idx % 2 === 0) {
                        doc.setFillColor(250, 250, 250);
                        doc.rect(20, yPosition - 4, 170, 6, 'F');
                    }

                    doc.text(field.label, 25, yPosition);
                    doc.text(formatPdfMeasurementValue(measurement[field.key]), 140, yPosition);
                    yPosition += 6;
                });
            }

            if (measurement.notes && measurement.notes.trim() !== '') {
                yPosition += 3;
                if (yPosition > 265) {
                    doc.addPage();
                    yPosition = 20;
                }
                doc.setFontSize(9);
                doc.setFont(undefined, 'italic');
                const splitNotes = doc.splitTextToSize(`Notes: ${measurement.notes}`, 170);
                doc.text(splitNotes, 25, yPosition);
                doc.setFont(undefined, 'normal');
                yPosition += splitNotes.length * 4 + 5;
            }

            yPosition += 8;
        });
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `Page ${i} sur ${pageCount} - Généré le ${new Date().toLocaleDateString('fr-FR')}`,
            105,
            290,
            { align: 'center' }
        );
    }

    // Download
    const fileName = `${client.name.replace(/[^a-z0-9]/gi, '_')}_toutes_mesures.pdf`;
    doc.save(fileName);
};
